import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  type Unsubscribe
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/modules/firebase';

export type NotificationType = 'mention' | 'assignment' | 'approval' | 'subscription_activity' | 'shipment_milestone';

export interface UserNotification {
  id: string;
  eventId: string;
  type: NotificationType;
  priority: 'high' | 'normal';
  reasons: string[];
  actor: { uid: string; displayName: string };
  subject: { type: 'shipments' | 'ncr'; id: string; label: string };
  title: string;
  preview: string;
  actionPath: string;
  createdAt: Timestamp;
  readAt: Timestamp | null;
  isRead: boolean;
  archivedAt: Timestamp | null;
  isArchived: boolean;
}

export interface NotificationPreferences {
  slackAssignments: boolean;
  pushMentions: boolean;
  pushAssignments: boolean;
  pushStarredActivity: boolean;
  pushShipmentMilestones: boolean;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  slackAssignments: true,
  pushMentions: true,
  pushAssignments: true,
  pushStarredActivity: false,
  pushShipmentMilestones: false
};

const markReadCallable = httpsCallable<Record<string, unknown>, { success: boolean; data: { count: number } }>(functions, 'markNotificationsRead');
const markUnreadCallable = httpsCallable<Record<string, unknown>, { success: boolean; data: { count: number } }>(functions, 'markNotificationsUnread');
const archiveCallable = httpsCallable<Record<string, unknown>, { success: boolean; data: { count: number } }>(functions, 'archiveNotifications');
const updatePreferencesCallable = httpsCallable<Partial<NotificationPreferences>, { success: boolean; data: { preferences: NotificationPreferences } }>(functions, 'updateNotificationPreferences');

const badgeCacheKey = (uid: string) => `JIDOKA_NOTIFICATION_COUNT_${uid}`;

export function getCachedUnreadCount(uid: string): number {
  try {
    const value = Number.parseInt(localStorage.getItem(badgeCacheKey(uid)) || '0', 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function cacheUnreadCount(uid: string, count: number): void {
  try {
    localStorage.setItem(badgeCacheKey(uid), String(Math.max(0, count)));
  } catch {
    // The realtime value remains authoritative when storage is unavailable.
  }
}

export function subscribeNotificationSummary(
  uid: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(doc(db, 'users', uid, 'notificationState', 'inbox'), snapshot => {
    const count = Math.max(0, Number(snapshot.data()?.unreadCount || 0));
    cacheUnreadCount(uid, count);
    callback(count);
  }, error => onError?.(error));
}

export function subscribeRecentNotifications(
  uid: string,
  callback: (notifications: UserNotification[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const recentQuery = query(
    collection(db, 'users', uid, 'notifications'),
    where('isArchived', '==', false),
    orderBy('createdAt', 'desc'),
    limit(30)
  );
  return onSnapshot(recentQuery, snapshot => {
    callback(snapshot.docs.map(item => ({ id: item.id, ...item.data() } as UserNotification)));
  }, error => onError?.(error));
}

export function subscribeNotifications(
  uid: string,
  archived: boolean,
  callback: (notifications: UserNotification[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const notificationsQuery = query(
    collection(db, 'users', uid, 'notifications'),
    where('isArchived', '==', archived),
    orderBy('createdAt', 'desc'),
    limit(100)
  );
  return onSnapshot(notificationsQuery, snapshot => {
    callback(snapshot.docs.map(item => ({ id: item.id, ...item.data() } as UserNotification)));
  }, error => onError?.(error));
}

export async function markNotificationsRead(input: Record<string, unknown>): Promise<number> {
  const result = await markReadCallable(input);
  return result.data.data.count;
}

export async function markNotificationRead(id: string): Promise<number> {
  return markNotificationsRead({ ids: [id] });
}

export async function markNotificationUnread(id: string): Promise<number> {
  const result = await markUnreadCallable({ ids: [id] });
  return result.data.data.count;
}

export async function markSubjectNotificationsRead(subjectType: string, subjectId: string): Promise<number> {
  return markNotificationsRead({ subjectType, subjectId });
}

export async function archiveNotifications(input: Record<string, unknown>): Promise<number> {
  const result = await archiveCallable(input);
  return result.data.data.count;
}

export async function saveNotificationPreferences(patch: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
  const result = await updatePreferencesCallable(patch);
  return result.data.data.preferences;
}

export function subscribeNotificationPreferences(
  uid: string,
  callback: (preferences: NotificationPreferences) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(doc(db, 'users', uid, 'notificationSettings', 'preferences'), snapshot => {
    callback({ ...DEFAULT_NOTIFICATION_PREFERENCES, ...(snapshot.data() || {}) });
  }, error => onError?.(error));
}

declare global {
  interface Window {
    JidokaNotifications: {
      markSubjectRead: typeof markSubjectNotificationsRead;
    };
  }
}
